/**
 * 68.43, radar coverage in the command view (the owner, Oct 20: "Would be cool to have CIWs or any radar enabled system to
 * show in command view a radius of their coverage modulated by terrain"). For each radar the side may draw — its own standing
 * mounts, and the enemy's only while the side knows them (core/mountIntel.ts knownMounts: what its eyes saw, never the truth
 * of a mount it has not found) — two pictures (the owner's ruling on the first: "maybe a little too busy, would be cool if
 * that was toggleable and the basic always on render was just the circular radius (keeping terrain modulation) and the blue
 * extant regions … could be kept as some radar detail mode for when air combat is more developed"):
 *  - **the reach** (always, wherever there are mounts): one grid at the low height out to the gun's reach — the edge where the
 *    gun can engage, its reach ∩ what the radar resolves (rendering/radarReachView.ts draws it once, in planet space);
 *  - **the detail** (the Lab row Radar detail, R in the command view): the full grid — what the radar resolves at each
 *    height out to its range, and the reach within it (rendering/radarCoverageDraw.ts, on the overlay).
 *
 * **The sim's own rule, sampled.** No twin of the radar's sight: each system in `RADAR_SYSTEMS` (keyed by the structure type
 * that carries it, so a later radar plugs in with an entry) answers "does it resolve a point" by the sim's function — for the
 * point defence, `pdCandidates` asked about a probe round at the point, coming toward the head (its range, its minimum
 * elevation, `personLineOfSight` from the head: the coarse ground, the fine relief, digs, craterlets, terraces and the
 * horizon). Smoke is left out: it blows over in seconds, the picture is the ground's.
 *
 * **The grid.** Polar round the mount: `COVER.bearings` bearings × rings `COVER.ringPeople` apart out to the radar's range; a
 * cell is sampled at its centre `h` people over the ground the sim stands on, for each height in `COVER.heights`; a cell
 * holds bit 1 (resolved) and bit 2 (resolved and within the gun's reach of the head).
 *
 * **Cost.** A mount's grid is a few thousand sight lines (tens of ms), so `RadarCoverage.update` samples within a budget a
 * frame, ring by ring outward (the drawing shows what is done), and keeps each grid until the ground near the mount
 * changes: a new crater in the log, a new or deepened craterlet or dig within the radar's range of it.
 */
import type { GameState, PlanetState, StructureState, Vec3, ActionShell } from '../types';
import { DROPSHIP } from '../constants/action';
import { pdCandidates, radarEye, variantOf, pdOn } from '../core/pointDefence';
import { knownMounts } from '../core/mountIntel';
import { structureTeam } from '../core/body';
import { partHealth, markedParts } from '../core/parts';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { personSurfaceRadius } from '../core/fineTerrain';

let settingOverride: boolean | null = null;
/** The Lab menu's row, set before it launches the match in the page (null: the URL decides). */
export function setRadarCoverage(v: boolean | null): void { settingOverride = v; }
/** The Lab row Radar detail at the start (the menu's, or `?radarcover=1`): off unless set; R flips it in the command view. */
export function radarCoverageSetting(): boolean {
  if (settingOverride !== null) return settingOverride;
  try { const v = new URLSearchParams(location.search).get('radarcover'); return v === '1' || v === 'true' || v === 'on'; } catch { return false; }
}
/** The sampling's budget a frame while the command view shows (ms). */
export const RADAR_MS = 2;

/** The grid: bearings round the mount, the rings' spacing (people), the flight heights over the ground (people). */
export const COVER = {
  bearings: 72, ringPeople: 4,
  /** low: a rocket's or a lobbed round's last stretch; high: the dropships' cruise */
  heights: [10, DROPSHIP.cruisePeople] as readonly number[],
  /** past the radar's range, a change to the ground this far out still re-samples it (people: a crater's rim) */
  marginPeople: 10,
} as const;

/** A radar-carrying system: its range and its gun's reach (people), and the sim's own answer to "does it resolve `pos`". */
export interface RadarSystem {
  label(s: StructureState): string;
  range(s: StructureState): number;
  reach(s: StructureState): number;
  /** Null when it cannot resolve anything (no enemy to probe as, or blind). */
  resolver(state: GameState, s: StructureState): ((pos: Vec3) => boolean) | null;
}

export const RADAR_SYSTEMS: Partial<Record<StructureState['type'], RadarSystem>> = {
  pointDefence: {
    label: s => (s.pd?.variant === 'ciws' ? 'CIWS' : s.pd?.variant === 'site' ? 'AD site' : 'C-RAM'),   // (69.50 step 2a: the forward air-defence site)
    range: s => variantOf(s).radar,
    reach: s => variantOf(s).reach,
    resolver(state, s) {
      const team = structureTeam(state, s), foe = state.players.find(p => p.teamIndex !== team);
      if (!foe) return null;
      // a probe round of the enemy's at the point, coming at the head: the radar's range, elevation floor and sight decide
      const eye = radarEye(state, s);
      const shell: ActionShell = { id: -1, ownerId: foe.id, structureId: -1, weaponKey: 'standard', pos: eye, vel: { x: 0, y: 0, z: 0 }, ticks: 0 };
      const probe: GameState = { ...state, shells: [shell], bullets: [], dropships: undefined, lighters: undefined, smokes: undefined };
      return pos => {
        shell.pos = pos;
        shell.vel = { x: eye.x - pos.x, y: eye.y - pos.y, z: eye.z - pos.z };
        return pdCandidates(probe, s, 0, undefined, true).length > 0;   // (a probe: a site, whose targets are ships, asked its sight alone)
      };
    },
  },
};

/** One radar's grid. `cells[(h * bearings + b) * rings + i]`: bit 1 resolved, bit 2 also within the reach; `done` cells sampled (ring-major). */
export interface Coverage {
  /** 'reach': the low height out to the gun's reach (the basic picture); 'detail': every height out to the radar's range */
  kind: 'reach' | 'detail';
  id: number; planetIndex: number; team: number; own: boolean; label: string;
  /** the mount's up and two tangents (the bearing 0 along `e1`, a quarter turn along `e2`) */
  up: Vec3; e1: Vec3; e2: Vec3;
  /** people a ring, the rings, the bearings, the heights (people) */
  ringPeople: number; rings: number; bearings: number; heights: readonly number[];
  rangePeople: number; reachPeople: number;
  cells: Uint8Array; done: number;
}

const norm = (v: Vec3): Vec3 => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };
const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });

/** The unit direction at bearing `a` (radians from `e1` toward `e2`) and arc `theta` (radians) from the mount. */
export function coverDir(c: Pick<Coverage, 'up' | 'e1' | 'e2'>, a: number, theta: number): Vec3 {
  const ca = Math.cos(a), sa = Math.sin(a), ct = Math.cos(theta), st = Math.sin(theta);
  return {
    x: c.up.x * ct + (c.e1.x * ca + c.e2.x * sa) * st,
    y: c.up.y * ct + (c.e1.y * ca + c.e2.y * sa) * st,
    z: c.up.z * ct + (c.e1.z * ca + c.e2.z * sa) * st,
  };
}

/** The radars the side may draw: its own standing ones, and the enemy's it knows (as it knows them). An own mount with its radar head gone is blind: none. */
export function radarsFor(state: GameState, team: number): Array<{ s: StructureState; sys: RadarSystem; own: boolean }> {
  const out: Array<{ s: StructureState; sys: RadarSystem; own: boolean }> = [];
  for (const s of state.structures) {
    const sys = RADAR_SYSTEMS[s.type];
    if (!sys || s.health <= 0 || structureTeam(state, s) !== team) continue;
    if (state.config.partialDestruction && markedParts(s.type).length && partHealth(s, 0) <= 0) continue;
    out.push({ s, sys, own: true });
  }
  for (const s of knownMounts(state, team)) { const sys = RADAR_SYSTEMS[s.type]; if (sys) out.push({ s, sys, own: false }); }
  return out;
}

/**
 * The overlay's line when the row is on and nothing is drawn (the owner, Oct 20: "Like nothing indicating that it's on, game
 * is normal"): why — no point defence in this match, no board for it to stand on, the side's own radars down and none of
 * theirs seen — or, at the planet table's stops (`bare`, where it is not drawn), where to look. Null when it draws.
 */
export function radarNote(state: GameState, drawn: number, bare: boolean): string | null {
  if (!pdOn(state.config)) return 'Radar detail: no radars (Point defence is off in the Lab, and Air-defence sites)';
  if (!state.structures.some(s => RADAR_SYSTEMS[s.type])) return 'Radar detail: no radars (point defence stands at the bases: set the Foothold board)';
  if (!drawn) return 'Radar detail: your radars are down, and none of theirs seen';
  if (bare) return 'Radar detail: wheel in from the table to see it';
  return null;
}

/** A fresh, unsampled grid for a radar: the reach's (the low height, out to the gun's reach) or the detail's (every height, out to the range). */
export function newCoverage(state: GameState, s: StructureState, sys: RadarSystem, own: boolean, kind: Coverage['kind'] = 'detail'): Coverage {
  const up = norm(s.surfaceDir), ax = Math.abs(up.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
  const e1 = norm(cross(up, ax)), e2 = cross(up, e1);
  const heights = kind === 'reach' ? COVER.heights.slice(0, 1) : COVER.heights;
  const rangePeople = kind === 'reach' ? sys.reach(s) : sys.range(s), rings = Math.max(1, Math.ceil(rangePeople / COVER.ringPeople));
  return {
    kind, id: s.id, planetIndex: s.planetIndex, team: structureTeam(state, s) ?? -1, own, label: sys.label(s), up, e1, e2,
    ringPeople: rangePeople / rings, rings, bearings: COVER.bearings, heights,
    rangePeople, reachPeople: sys.reach(s), cells: new Uint8Array(heights.length * COVER.bearings * rings), done: 0,
  };
}

/**
 * Sample up to `maxCells` more of a grid (or until `deadline`, a performance.now() time), ring by ring outward, each ring
 * at every height and bearing. True when the grid is complete.
 */
export function sampleCoverage(state: GameState, s: StructureState, sys: RadarSystem, c: Coverage, maxCells: number, deadline = Infinity): boolean {
  const total = c.cells.length;
  if (c.done >= total) return true;
  const resolves = sys.resolver(state, s);
  const planet = state.planets[c.planetIndex] as PlanetState, ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const eye = radarEye(state, s), reach = c.reachPeople * person, H = c.heights.length, B = c.bearings;
  let n = 0;
  while (c.done < total && n < maxCells) {
    // ring-major order: k = (i * H + h) * B + b
    const k = c.done, b = k % B, h = Math.floor(k / B) % H, i = Math.floor(k / (B * H));
    const dir = coverDir(c, (b / B) * 2 * Math.PI, ((i + 0.5) * c.ringPeople * person) / planet.radius);
    const r = personSurfaceRadius(planet, dir, ps) + c.heights[h] * person;
    const pos = { x: planet.position.x + dir.x * r, y: planet.position.y + dir.y * r, z: planet.position.z + dir.z * r };
    let v = 0;
    if (resolves && resolves(pos)) { v = 1; if (Math.hypot(pos.x - eye.x, pos.y - eye.y, pos.z - eye.z) <= reach) v |= 2; }
    c.cells[(h * B + b) * c.rings + i] = v;
    c.done++; n++;
    if ((n & 31) === 0 && performance.now() > deadline) break;
  }
  return c.done >= total;
}

/** Where the ground changed on a planet since the last look: the new edits' points and new or changed craterlets', digs', terraces' directions; `all` when the heightmap moved with no edit to say where. */
interface GroundSeen { heightmap: Float32Array; digs: unknown[] | undefined; craterlets: unknown[] | undefined; terraces: unknown[] | undefined }
const seenOf = (p: PlanetState): GroundSeen => ({ heightmap: p.heightmap, digs: p.digs, craterlets: p.craterlets, terraces: p.terraces });

/**
 * The side's radar coverage, kept between frames: `update` each frame the command view shows, with a budget in ms; it
 * returns the grids to draw (complete or partly sampled) — the reach's always, the detail's when asked. One per match (the host's).
 */
export class RadarCoverage {
  private grids = new Map<string, Coverage>();
  private seen = new Map<number, GroundSeen>();
  private edits = 0;
  /** Sampled cells over the grids' life (the readout) and the last update's cost (ms). */
  sampled = 0; lastMs = 0;

  update(state: GameState, team: number, budgetMs: number, detail = false): { reach: Coverage[]; detail: Coverage[] } {
    const t0 = performance.now();
    if (!state.structures.some(s => RADAR_SYSTEMS[s.type])) { this.lastMs = 0; return { reach: [], detail: [] }; }   // (no radar in the match: nothing to do)
    this.invalidate(state);
    const radars = radarsFor(state, team);
    const out = { reach: [] as Coverage[], detail: [] as Coverage[] };
    const deadline = t0 + budgetMs;
    // the reach's grids first (own, then the known enemy's), then the detail's; each sampled while time is left
    for (const kind of detail ? (['reach', 'detail'] as const) : (['reach'] as const)) {
      for (const r of radars) {
        const key = `${r.s.id}:${kind}`;
        let c = this.grids.get(key);
        if (!c || c.team !== (structureTeam(state, r.s) ?? -1) || c.own !== r.own) { c = newCoverage(state, r.s, r.sys, r.own, kind); this.grids.set(key, c); }
        if (c.done < c.cells.length && performance.now() < deadline) {
          const before = c.done;
          sampleCoverage(state, r.s, r.sys, c, Infinity, deadline);
          this.sampled += c.done - before;
        }
        out[kind].push(c);
      }
    }
    this.lastMs = performance.now() - t0;
    return out;
  }

  /** Drop the grids the ground changed under (within a grid's range and the margin of its mount). */
  private invalidate(state: GameState): void {
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    const touched: Array<{ planetIndex: number; dir: Vec3 | null; radius: number }> = [];
    const log = state.terrainEdits ?? [];
    for (let k = this.edits; k < log.length; k++) {
      const e = log[k], p = state.planets[e.planetIndex];
      if (p) touched.push({ planetIndex: e.planetIndex, dir: norm({ x: e.point.x - p.position.x, y: e.point.y - p.position.y, z: e.point.z - p.position.z }), radius: e.radius });
    }
    const logged = new Set(touched.map(t => t.planetIndex));
    this.edits = log.length;
    state.planets.forEach((p, pi) => {
      const was = this.seen.get(pi), now = seenOf(p as PlanetState);
      this.seen.set(pi, now);
      if (!was) return;
      if (was.heightmap !== now.heightmap && !logged.has(pi)) touched.push({ planetIndex: pi, dir: null, radius: 0 });
      for (const key of ['digs', 'craterlets', 'terraces'] as const) {
        const a = was[key], b = now[key];
        if (a === b || !b) continue;
        const old = new Set(a ?? []);
        for (const x of b as Array<{ dir: Vec3 }>) if (!old.has(x)) touched.push({ planetIndex: pi, dir: norm(x.dir), radius: 0 });
      }
    });
    if (!touched.length) return;
    for (const [id, c] of this.grids) {
      const planet = state.planets[c.planetIndex];
      const near = touched.some(t => {
        if (t.planetIndex !== c.planetIndex) return false;
        if (!t.dir) return true;
        const dot = Math.max(-1, Math.min(1, t.dir.x * c.up.x + t.dir.y * c.up.y + t.dir.z * c.up.z));
        return Math.acos(dot) * planet.radius <= (c.rangePeople + COVER.marginPeople) * person + t.radius;
      });
      if (near) this.grids.delete(id);
    }
  }
}
